import { DeprecatedStatusCode, HttpVerb, } from '@standardnotes/responses';
import { isString } from '@standardnotes/utils';
import { SnjsVersion } from '@Lib/Version';
import { AbstractService, API_MESSAGE_RATE_LIMITED, UNKNOWN_ERROR, } from '@standardnotes/services';
import { Environment } from '@standardnotes/models';
const REQUEST_READY_STATE_COMPLETED = 4;
/**
 * A non-SNJS specific wrapper for XMLHttpRequests
 */
export class DeprecatedHttpService extends AbstractService {
    constructor(environment, appVersion, internalEventBus) {
        super(internalEventBus);
        this.environment = environment;
        this.appVersion = appVersion;
        this.internalEventBus = internalEventBus;
    }
    async getAbsolute(url, params, authentication) {
        return this.runHttp({ url, params, verb: HttpVerb.Get, authentication });
    }
    async postAbsolute(url, params, authentication) {
        return this.runHttp({ url, params, verb: HttpVerb.Post, authentication });
    }
    async putAbsolute(url, params, authentication) {
        return this.runHttp({ url, params, verb: HttpVerb.Put, authentication });
    }
    async patchAbsolute(url, params, authentication) {
        return this.runHttp({ url, params, verb: HttpVerb.Patch, authentication });
    }
    async deleteAbsolute(url, params, authentication) {
        return this.runHttp({ url, params, verb: HttpVerb.Delete, authentication });
    }
    async runHttp(httpRequest) {
        const request = this.createXmlRequest(httpRequest);
        return this.runRequest(request, this.createRequestBody(httpRequest));
    }
    createRequestBody(httpRequest) {
        if (httpRequest.params !== undefined &&
            [HttpVerb.Post, HttpVerb.Put, HttpVerb.Patch, HttpVerb.Delete].includes(httpRequest.verb)) {
            return JSON.stringify(httpRequest.params);
        }
        return httpRequest.rawBytes;
    }
    createXmlRequest(httpRequest) {
        var _a;
        const request = new XMLHttpRequest();
        if (httpRequest.params && httpRequest.verb === HttpVerb.Get && Object.keys(httpRequest.params).length > 0) {
            httpRequest.url = this.urlForUrlAndParams(httpRequest.url, httpRequest.params);
        }
        request.open(httpRequest.verb, httpRequest.url, true);
        request.responseType = (_a = httpRequest.responseType) !== null && _a !== void 0 ? _a : '';
        if (!httpRequest.external) {
            request.setRequestHeader('X-SNJS-Version', SnjsVersion);
            const appVersionHeaderValue = `${Environment[this.environment]}-${this.appVersion}`;
            request.setRequestHeader('X-Application-Version', appVersionHeaderValue);
            if (httpRequest.authentication) {
                request.setRequestHeader('Authorization', 'Bearer ' + httpRequest.authentication);
            }
        }
        let contenTypeIsSet = false;
        if (httpRequest.customHeaders && httpRequest.customHeaders.length > 0) {
            httpRequest.customHeaders.forEach(({ key, value }) => {
                request.setRequestHeader(key, value);
                if (key === 'Content-Type') {
                    contenTypeIsSet = true;
                }
            });
        }
        if (!contenTypeIsSet && !httpRequest.external) {
            request.setRequestHeader('Content-Type', 'application/json');
        }
        return request;
    }
    async runRequest(request, body) {
        return new Promise((resolve, reject) => {
            request.onreadystatechange = () => {
                this.stateChangeHandlerForRequest(request, resolve, reject);
            };
            request.send(body);
        });
    }
    stateChangeHandlerForRequest(request, resolve, reject) {
        var _a, _b, _c;
        if (request.readyState !== REQUEST_READY_STATE_COMPLETED) {
            return;
        }
        const httpStatus = request.status;
        const response = {
            status: httpStatus,
            headers: new Map(),
        };
        const responseHeaderLines = (_a = request
            .getAllResponseHeaders()) === null || _a === void 0 ? void 0 : _a.trim().split(/[\r\n]+/);
        responseHeaderLines === null || responseHeaderLines === void 0 ? void 0 : responseHeaderLines.forEach((responseHeaderLine) => {
            const parts = responseHeaderLine.split(': ');
            const name = parts.shift();
            const value = parts.join(': ');
            response.headers.set(name, value);
        });
        try {
            if (httpStatus !== DeprecatedStatusCode.HttpStatusNoContent) {
                let body;
                const contentTypeHeader = ((_b = response.headers) === null || _b === void 0 ? void 0 : _b.get('content-type')) || ((_c = response.headers) === null || _c === void 0 ? void 0 : _c.get('Content-Type'));
                if (contentTypeHeader === null || contentTypeHeader === void 0 ? void 0 : contentTypeHeader.includes('application/json')) {
                    body = JSON.parse(request.responseText);
                }
                else {
                    body = request.response;
                }
                /**
                 * v0 APIs do not have a `data` top-level object. In such cases, mimic
                 * the newer response body style by putting all the top-level
                 * properties inside a `data` object.
                 */
                if (!body.data) {
                    response.data = body;
                }
                if (!isString(body)) {
                    Object.assign(response, body);
                }
            }
        }
        catch (error) {
            console.error(error);
        }
        if (httpStatus >= DeprecatedStatusCode.HttpStatusMinSuccess &&
            httpStatus <= DeprecatedStatusCode.HttpStatusMaxSuccess) {
            resolve(response);
        }
        else {
            if (httpStatus === DeprecatedStatusCode.HttpStatusForbidden) {
                response.error = {
                    message: API_MESSAGE_RATE_LIMITED,
                    status: httpStatus,
                };
            }
            else if (response.error == undefined) {
                if (response.data == undefined || response.data.error == undefined) {
                    try {
                        response.error = { message: request.responseText || UNKNOWN_ERROR, status: httpStatus };
                    }
                    catch {
                        response.error = { message: UNKNOWN_ERROR, status: httpStatus };
                    }
                }
                else {
                    response.error = response.data.error;
                }
            }
            reject(response);
        }
    }
    urlForUrlAndParams(url, params) {
        const keyValueString = Object.keys(params)
            .map((key) => {
            return key + '=' + encodeURIComponent(params[key]);
        })
            .join('&');
        if (url.includes('?')) {
            return url + '&' + keyValueString;
        }
        else {
            return url + '?' + keyValueString;
        }
    }
}
